import React, { useState, useRef } from 'react';
import { Upload, Download, X } from 'lucide-react';
import api from '../../services/api';
import { field, ghost, primary, muted, rowBorder, errorText } from '../time/styles';

// HR → New joiners → Import from a spreadsheet. Pick a CSV or Excel file, see
// every row checked (nothing is created yet), then import.

const download = async () => {
    const r = await api.get('/lifecycle/joiners/import/template', { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'new-joiners-template.csv';
    a.click();
    URL.revokeObjectURL(url);
};

const JoinerImport = ({ checklists, say, onDone, onCancel }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [problem, setProblem] = useState('');
    const [busy, setBusy] = useState(false);
    const [checklistTemplateId, setChecklist] = useState('');
    const [sendInvites, setSendInvites] = useState(true);
    const [skipBad, setSkipBad] = useState(false);
    const [result, setResult] = useState(null);

    const body = () => {
        const f = new FormData();
        f.append('file', file);
        f.append('sendInvites', String(sendInvites));
        f.append('onlyValid', String(skipBad));
        if (checklistTemplateId) f.append('checklistTemplateId', checklistTemplateId);
        return f;
    };
    const latest = useRef(0);
    const choose = async (e) => {
        const chosen = e.target.files?.[0];
        e.target.value = ''; // choosing the same file again (after fixing it) must read it again
        const mine = ++latest.current;
        setPreview(null); setProblem(''); setResult(null); setFile(chosen || null);
        if (!chosen) return;
        setBusy(true);
        try {
            const f = new FormData();
            f.append('file', chosen);
            const r = await api.post('/lifecycle/joiners/import/preview', f, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (mine === latest.current) setPreview(r.data);
        } catch (err) { if (mine === latest.current) setProblem(errorText(err, 'Could not read that file')); }
        if (mine === latest.current) setBusy(false);
    };
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post('/lifecycle/joiners/import', body(), { headers: { 'Content-Type': 'multipart/form-data' } });
            setResult(r.data);
            if (r.data.created.length) {
                say(true, `${r.data.created.length} new joiner${r.data.created.length === 1 ? '' : 's'} added`);
                onDone();
            } else say(false, 'Nobody was added. See the reasons below.');
        } catch (err) { say(false, errorText(err, 'The import did not work')); }
        setBusy(false);
    };

    const c = preview?.counts;
    const importable = c ? (skipBad ? c.ok : c.withErrors === 0 ? c.total : 0) : 0;
    return (
        <div data-testid="joiner-import" style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <strong style={{ color: 'var(--text-main)' }}>Import from a spreadsheet</strong>
                <button style={ghost} aria-label="Close import" onClick={onCancel}><X size={14} /></button>
            </div>
            <p style={{ ...muted, margin: 0 }}>Up to 200 people from a CSV or Excel file. Name, personal email and joining date are required. Every row is checked first; nothing is created until you import.</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button style={ghost} onClick={() => download().catch(e => say(false, errorText(e, 'Could not download')))}><Download size={14} /> Download template</button>
                <label style={{ ...ghost, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer', position: 'relative' }}>
                    <Upload size={14} /> {file ? file.name : 'Choose file'}
                    <input type="file" accept=".csv,.xlsx,.xls" aria-label="Spreadsheet file" onChange={choose} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }} />
                </label>
            </div>
            {busy && !result && <p style={muted}>Working…</p>}
            {problem && <p role="alert" style={{ color: 'var(--danger, #b42318)', margin: 0 }}>{problem}</p>}
            {preview && !result && (
                <>
                    <p style={{ margin: 0 }} data-testid="import-counts">
                        {c.total} row{c.total === 1 ? '' : 's'}: <strong>{c.ok} ready</strong>{c.withErrors ? `, ${c.withErrors} with problems` : ''}{c.withWarnings ? `, ${c.withWarnings} with notes` : ''}.
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                            <thead><tr style={{ textAlign: 'left', ...muted }}><th style={{ padding: 4 }}>Row</th><th>Name</th><th>Email</th><th>Result</th></tr></thead>
                            <tbody>
                                {preview.rows.map(r => (
                                    <tr key={r.row} style={{ borderTop: rowBorder, verticalAlign: 'top' }}>
                                        <td style={{ padding: 4 }}>{r.row}</td><td>{r.name || '—'}</td><td>{r.email || '—'}</td>
                                        <td>
                                            {r.errors.map((e, i) => <div key={i} style={{ color: 'var(--danger, #b42318)' }}>✕ {e}</div>)}
                                            {r.warnings.map((w, i) => <div key={i} style={muted}>• {w}</div>)}
                                            {r.ok && r.warnings.length === 0 && <span style={muted}>Ready</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                        <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Onboarding checklist</span>
                            <select style={field} value={checklistTemplateId} onChange={e => setChecklist(e.target.value)}>
                                <option value="">None</option>
                                {checklists.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </label>
                        <label><input type="checkbox" checked={sendInvites} onChange={e => setSendInvites(e.target.checked)} /> Email each person their invite</label>
                        {c.withErrors > 0 && <label><input type="checkbox" checked={skipBad} onChange={e => setSkipBad(e.target.checked)} /> Skip rows with problems</label>}
                    </div>
                    <div>
                        <button className="btn btn-primary" style={primary} disabled={busy || importable === 0} onClick={run}>Import {importable} {importable === 1 ? 'person' : 'people'}</button>
                        {c.withErrors > 0 && !skipBad && <span style={{ ...muted, marginLeft: 10 }}>Fix the file and choose it again, or skip the rows with problems.</span>}
                    </div>
                </>
            )}
            {result && (
                <div data-testid="import-result">
                    <p style={{ margin: 0 }}><strong>{result.created.length} added</strong>{result.skipped.length ? `, ${result.skipped.length} skipped` : ''}{result.failed.length ? `, ${result.failed.length} failed` : ''}.{result.inviting ? ' Invites are being emailed.' : ''}</p>
                    {[...result.skipped, ...result.failed].map(r => <div key={r.row} style={muted}>Row {r.row} ({r.name}): {(r.errors || [r.error]).join('; ')}</div>)}
                    {result.created.filter(r => r.note).map(r => <div key={r.row} style={muted}>Row {r.row} ({r.name}): {r.note}</div>)}
                    <button style={{ ...ghost, marginTop: 8 }} onClick={onCancel}>Done</button>
                </div>
            )}
        </div>
    );
};

export default JoinerImport;
